import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, Check, Clock, KeyRound, ShieldCheck, UserCheck } from "lucide-react";
import { HelmLogo } from "@/components/helm-logo";
import { useCallback, useEffect, useState } from "react";
import z from "zod";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { API_BASE_URL } from "@/lib/consts";
import {
	trackSignupInvitePageViewed,
	trackSignupMethodSelected,
	trackUserSignedUp,
} from "@/lib/analytics-events";

// --- Types ---

interface InviteValidationSuccess {
	status: "valid";
	email: string;
	candidate_name: string | null;
	invite_id: number;
	invite_type: "admin" | "team" | null;
	// [[I-006]] Backend tells us whether the invitee still needs to set
	// a password. False = existing user with credentials already on file;
	// skip the create-account form and POST /invites/accept directly.
	// The accept endpoint never mints tokens on its own — the response's
	// redirect_url always points at /auth/phone-verification so the SMS
	// OTP step still gates token issuance.
	requires_password?: boolean;
}

interface InviteValidationError {
	status: "expired" | "used" | "invalid";
	message: string;
}



// --- Route definition ---

export const Route = createFileRoute("/auth/accept-invite")({
	component: AcceptInvitePage,
	validateSearch: (search) =>
		z.object({ token: z.string().min(1).optional() }).parse(search),
});

// --- Main component ---

function AcceptInvitePage() {
	const { token } = Route.useSearch();
	const [state, setState] = useState<
		| { phase: "loading" }
		| { phase: "accepting" }
		| { phase: "valid"; data: InviteValidationSuccess }
		| { phase: "error"; data: InviteValidationError }
	>({ phase: "loading" });

	const autoAccept = useCallback(async (token: string) => {
		// [[I-006]] Skip the create-account form, POST /invites/accept
		// ourselves, and follow the backend's redirect_url
		// (always /auth/phone-verification — the OTP step issues tokens).
		setState({ phase: "accepting" });
		try {
			const res = await fetch(`${API_BASE_URL}/invites/accept`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ token }),
			});
			const body = await res.json().catch(() => null);

			if (res.ok && body?.redirect_url) {
				trackUserSignedUp("email");
				window.location.href = body.redirect_url;
				return;
			}

			// Real failure — backend didn't accept. Surface the message.
			const detail = body?.detail;
			const message =
				typeof detail === "string"
					? detail
					: detail?.message ??
					  "Unable to accept this invite. Please try again.";
			setState({
				phase: "error",
				data: { status: "invalid", message },
			});
		} catch {
			setState({
				phase: "error",
				data: { status: "invalid", message: "This link is invalid" },
			});
		}
	}, []);

	const validate = useCallback(async (token: string) => {
		try {
			const res = await fetch(`${API_BASE_URL}/auth/validate-invite`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ token }),
			})

			if (res.ok) {
				const data: InviteValidationSuccess = await res.json();
				// [[I-006]] Skip the password form for already-gated users.
				if (data.requires_password === false) {
					autoAccept(token);
					return;
				}
				setState({ phase: "valid", data });
				return
			}

			// Error responses return detail as JSON object
			const body = await res.json().catch(() => null);
			const detail = body?.detail;

			if (detail && typeof detail === "object" && detail.status) {
				setState({ phase: "error", data: detail as InviteValidationError });
			} else {
				setState({
					phase: "error",
					data: { status: "invalid", message: "This link is invalid" },
				})
			}
		} catch {
			setState({
				phase: "error",
				data: { status: "invalid", message: "This link is invalid" },
			})
		}
	}, [autoAccept]);

	useEffect(() => {
		if (!token) {
			setState({
				phase: "error",
				data: { status: "invalid", message: "This link is invalid" },
			})
			return
		}
		validate(token);
	}, [token, validate]);

	if (state.phase === "loading") {
		return <LoadingView />;
	}

	if (state.phase === "accepting") {
		return <LoadingView message="Setting up your access..." />;
	}

	if (state.phase === "error") {
		return <ErrorView status={state.data.status} message={state.data.message} />;
	}

	return <AuthMethodSelection data={state.data} token={token!} />;
}

// --- Loading state ---

function LoadingView({ message }: { message?: string } = {}) {
	return (
		<div className="min-h-screen flex items-center justify-center">
			<div className="flex flex-col items-center gap-4">
				<Spinner className="size-8 text-primary" />
				<p className="text-muted-foreground text-sm">
					{message ?? "Validating your invite..."}
				</p>
			</div>
		</div>
	)
}

// --- Error states ---

function ErrorView({ status }: { status: string; message: string }) {
	if (status === "expired") {
		return (
			<ErrorLayout
				icon={<Clock className="size-8 text-amber-500" />}
				title="This link has expired"
				description="Contact your sales representative for a new invite link."
			/>
		)
	}

	if (status === "used") {
		return (
			<ErrorLayout
				icon={<UserCheck className="size-8 text-blue-500" />}
				title="Account already set up"
				description="This invite has already been used to create an account."
				action={
					<Link to="/login">
						<Button size="lg" className="w-full">
							Go to Login
						</Button>
					</Link>
				}
			/>
		)
	}

	return (
		<ErrorLayout
			icon={<AlertCircle className="size-8 text-destructive" />}
			title="This link is invalid"
			description="Please check the link or contact support for assistance."
		/>
	)
}

function ErrorLayout({
	icon,
	title,
	description,
	action,
}: {
	icon: React.ReactNode;
	title: string;
	description: string;
	action?: React.ReactNode;
}) {
	return (
		<div className="min-h-screen flex items-center justify-center px-6">
			<div className="w-full max-w-sm text-center space-y-6">
				<div className="flex justify-center">{icon}</div>
				<div className="space-y-2">
					<h1 className="text-2xl font-semibold text-foreground">{title}</h1>
					<p className="text-muted-foreground text-[15px]">{description}</p>
				</div>
				{action}
			</div>
		</div>
	)
}

// --- Auth method selection (valid token) ---

function AuthMethodSelection({
	data,
	token,
}: {
	data: InviteValidationSuccess;
	token: string;
}) {
	const navigate = useNavigate();
	const [googleLoading, setGoogleLoading] = useState(false);

	useEffect(() => {
		trackSignupInvitePageViewed(data.invite_type);
	}, [data.invite_type]);

	const handleGoogleAuth = async () => {
		trackSignupMethodSelected("google");
		setGoogleLoading(true);
		try {
			const res = await fetch(
				`${API_BASE_URL}/auth/google/authorize?invite_token=${encodeURIComponent(token)}`,
			)
			if (!res.ok) {
				throw new Error("Failed to get authorization URL");
			}
			const { authorize_url } = await res.json();
			// Full page redirect to Google consent screen
			window.location.href = authorize_url;
		} catch {
			setGoogleLoading(false);
		}
	}

	const handlePasswordAuth = () => {
		trackSignupMethodSelected("password");
		if (data.invite_type === "team") {
			navigate({ to: "/auth/register-invite", search: { token } });
		} else {
			navigate({ to: "/auth/create-password", search: { token } });
		}
	}

	return (
		<div className="min-h-screen flex">
			{/* Left Panel - Branding / Trust Indicators */}
			<div className="hidden lg:flex lg:w-[45%] bg-primary items-center justify-center p-12">
				<div className="w-full max-w-md space-y-8 text-primary-foreground">
					<HelmLogo size="small" color="hsl(0 0% 100%)" />

					<div className="space-y-6">
						<h3 className="text-xl font-semibold">
							Welcome to your campaign toolkit
						</h3>
						<div className="space-y-4">
							<TrustIndicator text="Personalized campaign strategy" />
							<TrustIndicator text="Daily campaign briefings" />
							<TrustIndicator text="Voter outreach tools" />
							<TrustIndicator text="Secure and private" />
						</div>
					</div>

					<p className="text-primary-foreground/60 text-sm">
						A human system for independent participation in public life
					</p>
				</div>
			</div>

			{/* Right Panel - Auth Method Selection */}
			<div className="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:px-12">
				<div className="w-full max-w-md space-y-8">
					{/* Header */}
					<div className="space-y-2">
						<h1 className="text-[32px] font-semibold tracking-tight text-foreground">
							Create Your Account
						</h1>
						<p className="text-[15px] text-muted-foreground">
							Setting up account for:{" "}
							<span className="font-medium text-foreground">{data.email}</span>
						</p>
					</div>

					{/* Google Button */}
					<div className="space-y-4">
						<Button
							size="xl"
							variant="outline"
							className="w-full gap-3 text-[15px] font-medium"
							onClick={handleGoogleAuth}
							disabled={googleLoading}
						>
							{googleLoading ? (
								<Spinner className="size-5" />
							) : (
								<GoogleIcon />
							)}
							{googleLoading ? "Redirecting to Google..." : "Continue with Google"}
						</Button>
					</div>

					{/* Divider */}
					<div className="flex items-center gap-4">
						<Separator className="flex-1" />
						<span className="text-xs text-muted-foreground uppercase tracking-wider">
							or
						</span>
						<Separator className="flex-1" />
					</div>

					{/* Password Button */}
					<Button
						size="lg"
						variant="secondary"
						className="w-full text-[15px] font-medium gap-2"
						onClick={handlePasswordAuth}
					>
						<KeyRound className="size-4" />
						Create Password Instead
					</Button>

					{/* Security note */}
					<div className="flex items-start gap-2 text-xs text-muted-foreground pt-2">
						<ShieldCheck className="size-4 shrink-0 mt-0.5" />
						<span>
							Your data is encrypted and never shared. By continuing, you agree
							to our Terms of Service and Privacy Policy.
						</span>
					</div>
				</div>
			</div>
		</div>
	)
}

function TrustIndicator({ text }: { text: string }) {
	return (
		<div className="flex items-center gap-3">
			<div className="shrink-0 h-5 w-5 rounded-full bg-primary-foreground/20 flex items-center justify-center">
				<Check className="h-3 w-3 text-primary-foreground" />
			</div>
			<span className="text-[15px] text-primary-foreground/90">{text}</span>
		</div>
	)
}

function GoogleIcon() {
	return (
		<svg viewBox="0 0 24 24" className="size-5" aria-hidden="true">
			<path
				d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
				fill="#4285F4"
			/>
			<path
				d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
				fill="#34A853"
			/>
			<path
				d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
				fill="#FBBC05"
			/>
			<path
				d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
				fill="#EA4335"
			/>
		</svg>
	)
}
